// GENERATED by the Phase-0 crosswalk rebuild (docs/plans/legacy-to-policy.crosswalk.json).
// Source-of-truth for the legacy -> Policy-worksheet migration. Keep in sync with the crosswalk;
// the reconciliation test asserts every live catalog mapKey appears here exactly once.
import { classifyLegacyFlagOrFeature } from "./Cortex.MigrationSpecs";
import { legacySequenceMigratedKeys } from "./Cortex.SequenceMigrationSpecs";

/** A deterministic (T1) legacy -> policy mapping. `transform` yields the raw value for `normalizeToField`. */
export interface PolicyMigrationT1Spec {
  /** Target policy field, `policyKey.itemKey`. */
  mapKey: string;
  /** Surviving legacy source vars, read at the same scope (first non-empty wins). */
  legacyKeys: string[];
  /** Coerce a legacy value into the policy field's raw value (null = no confident value). */
  transform: (raw: string | null) => string | null;
}

/** Legacy boolean, tolerating either a serialized feature unit or a flag string, to "1"/"0".
 * The blob tolerance this comment has always claimed lives in `classifyLegacyFlagOrFeature`; reading
 * through plain `classifyLegacyFlag` made every blob-shaped row return null, i.e. "no confident
 * value", which silently dropped the field from the migration. */
function toggle(raw: string | null): string | null {
  const c = classifyLegacyFlagOrFeature(raw);
  return c === "on" ? "1" : c === "off" ? "0" : null;
}
/** Unmanned facility = the inverse of an on-site-manager flag. */
function invertToggle(raw: string | null): string | null {
  const t = toggle(raw);
  return t == null ? null : t === "1" ? "0" : "1";
}
/** A "capability offered" flag -> self-serve vs team-only channel. */
function portalTeam(raw: string | null): string | null {
  const t = toggle(raw);
  return t == null ? null : t === "1" ? "portal-self-serve" : "team-only";
}
/** Legacy FMS gate-code channel (`SMS` / `Email` / `Both`) → how codes reach the tenant. */
function gateCodeChannel(raw: string | null): string | null {
  switch ((raw ?? "").trim().toLowerCase()) {
    case "sms":
      return "sms";
    case "email":
      return "email";
    case "both":
      return "sms,email";
    default:
      return null;
  }
}
/** Free text passthrough (trimmed). */
function passText(raw: string | null): string | null {
  return raw && raw.trim() ? raw.trim() : null;
}

/** Map the free-text legacy `LockTypes` (Amenities) to the lock-style select; multiple/unknown → "other". */
function lockStyleOption(raw: string | null): string | null {
  const v = (raw ?? "").trim().toLowerCase();
  if (!v) return null;
  const matches = [v.includes("disc") && "disc-lock", v.includes("cylinder") && "cylinder-lock", v.includes("padlock") && "standard-padlock"].filter(
    (m): m is string => Boolean(m)
  );
  return matches.length === 1 ? matches[0] : "other";
}
/** The raw `LockTypes` text, kept only when it didn't map to a single option, so exact wording survives under "Other". */
function lockStyleOther(raw: string | null): string | null {
  const v = (raw ?? "").trim();
  return v && lockStyleOption(raw) === "other" ? v : null;
}

/** Which T1 transforms pass through free-form text rather than reduce to a toggle/enum code — the
 * source of truth for "is this T1 spec's legacy value a toggle or free text", used by the
 * customization report to classify a not-carrying-over item without re-deriving it elsewhere. */
export const POLICY_T1_FREE_TEXT_TRANSFORMS = new Set<PolicyMigrationT1Spec["transform"]>([passText, lockStyleOther]);

/** LEGACY_PROSE_KEYS entries that are pure on/off gates (their own value is never real prose) whose
 * informational content is ALREADY independently reported via a sibling key elsewhere in the
 * crosswalk — reporting the gate's own row as "scanned, no match" is structural noise (a raw "1"/"0"
 * can never satisfy a citation match), not a real gap. Traced against real org data: HasAuctions ->
 * FeatureAuction1/AuctionsURL, HasGateCodeIntegration -> AccessGateCode1/2 + LLMAccessGateCode,
 * HasInsuranceWebsiteURL -> InsuranceURL/InsuranceDisplayName, HasPayBillSpotPaymentPages ->
 * AccountPartialPayment1 + AfterNavigateAccountLoginPayBill — each independently scanned/reported on
 * its own. HasMobileApp deliberately excluded: it had no sibling in the crosswalk, so it stays visible
 * (as a toggle, not misleadingly free-text) rather than being silently dropped. ENG-3124 since gave it
 * a Details destination (Links → Mobile App), which the report now shows directly. */
export const REDUNDANT_PROSE_BOOLEAN_KEYS = new Set([
  "HasAuctions",
  "HasGateCodeIntegration",
  "HasInsuranceWebsiteURL",
  "HasPayBillSpotPaymentPages"
]);

/** Legacy key families owned by a migration surface OUTSIDE this report's three branches (Details,
 * Policy T1, Policy T2). `customizationReport()` excludes a matching key entirely rather than
 * reporting it as a permanent gap, so the Unmigrated Content report doesn't go stale once another
 * migration step handles these keys. This is the ONE place to update, not a change to
 * customizationReport()'s own branch-matching logic.
 *
 * The Sequences step (ENG-3739) owns the legacy sequence keys it migrates. Review and referral asks
 * and staff receipts are not among them, so this report keeps archiving those. */
export const EXTERNALLY_OWNED_KEY_FAMILIES: Set<string> = new Set(legacySequenceMigratedKeys());

/** T1 — deterministic legacy -> policy maps (no LLM). */
export const POLICY_T1_SPECS: PolicyMigrationT1Spec[] = [
  { mapKey: "access-procedures.unmanned-facility", legacyKeys: ["HasOnSiteManager", "CortexFeatureOnSiteManager"], transform: invertToggle },
  { mapKey: "account-info.password-reset", legacyKeys: ["AfterNavigateAccountLoginPassword"], transform: toggle },
  {
    mapKey: "account-info.portal-login-scope",
    legacyKeys: ["AfterNavigateAccountLoginMultipleAccounts", "AfterNavigateAccountLoginMultipleAccounts1"],
    transform: toggle
  },
  { mapKey: "account-info.portal-self-signup", legacyKeys: ["HasAccountLoginPage", "AfterNavigateAccountLoginCreateLogin"], transform: toggle },
  { mapKey: "account-info.username-recovery", legacyKeys: ["AfterNavigateAccountLoginUsername"], transform: toggle },
  {
    mapKey: "contact-info-update.update-method",
    legacyKeys: ["AfterNavigateAccountLoginUpdateContact", "AfterNavigateAccountLoginUpdateContact1"],
    transform: portalTeam
  },
  // The CortexFeature* key only exists once Details has written it, so the true legacy flag must be a
  // fallback or an org's first migration has nothing to read. Both spellings: the corrupted one is the
  // widely deployed registry key, but some orgs hold data only under the correct one.
  {
    mapKey: "damage-theft-claim.camera-footage",
    legacyKeys: ["CortexFeatureVideoSurveillance", "HasVideoSurveillanceveillance", "HasVideoSurveillance"],
    transform: toggle
  },
  // The FMS channel is a clean SMS / Email / Both setting, so it maps directly instead of through the scan.
  { mapKey: "gate-code.code-delivery", legacyKeys: ["FMSGateCodeChannel"], transform: gateCodeChannel },
  { mapKey: "insurance.in-house-plan-name", legacyKeys: ["InsuranceDisplayName"], transform: passText },
  { mapKey: "lease-changes.due-date-change", legacyKeys: ["HasChangeDueDate"], transform: toggle },
  { mapKey: "payments.multi-unit-billing", legacyKeys: ["AfterNavigateAccountLoginMultipleAccounts"], transform: toggle },
  { mapKey: "payments.payment-method-change", legacyKeys: ["AfterNavigateAccountLoginUpdateBilling"], transform: portalTeam },
  { mapKey: "pricing-policies.commercial-accounts", legacyKeys: ["HasBusinessStorage", "CortexFeatureBusinessStorage"], transform: toggle },
  { mapKey: "public-auctions.auction-platform-other", legacyKeys: ["AuctionsURL"], transform: passText },
  { mapKey: "storage-policies.business-storage", legacyKeys: ["CortexFeatureBusinessStorage", "HasBusinessStorage"], transform: toggle },
  { mapKey: "storage-policies.electrical-use", legacyKeys: ["CortexFeatureElectricityInUnit", "HasElectricityInUnit"], transform: toggle },
  // Lock style is the structured legacy `LockTypes` (Amenities), not LLM-extracted from bot prose.
  { mapKey: "storage-policies.lock-style", legacyKeys: ["LockTypes"], transform: lockStyleOption },
  { mapKey: "storage-policies.lock-style-other", legacyKeys: ["LockTypes"], transform: lockStyleOther },
  { mapKey: "storage-policies.locks-sold", legacyKeys: ["CortexFeaturePadlocks", "HasPadlocks"], transform: toggle },
  { mapKey: "vbp.tier-highest-name", legacyKeys: ["VBPName3"], transform: passText },
  { mapKey: "vbp.tier-lowest-name", legacyKeys: ["VBPName1"], transform: passText },
  { mapKey: "vbp.tier-middle-name", legacyKeys: ["VBPName2"], transform: passText },
  { mapKey: "vbp.vbp-used", legacyKeys: ["HasValueBasedPricing"], transform: toggle }
];

/** T2 — prose fields recovered by the shared LLM extractor over surviving legacy prose (always verify). */
export const POLICY_T2_MAPKEYS: string[] = [
  "access-procedures.after-hours-access",
  "access-procedures.entry-method",
  "access-procedures.fob-replacement",
  "access-procedures.fob-replacement-fee",
  "access-procedures.non-tenant-access",
  "billing-questions.adjustments-manager-approval",
  "billing-questions.monthly-statements",
  "billing-questions.past-lease-records",
  "billing-questions.receipts-on-request",
  "contact-info-update.comms-preferences",
  "contact-info-update.emergency-contact-required",
  "damage-theft-claim.follow-up-window",
  "damage-theft-claim.own-insurer",
  "damage-theft-claim.police-report",
  "damage-theft-claim.protection-plan-claims",
  "delinquency.grace-period",
  "delinquency.grace-period-days",
  "delinquency.late-fee",
  "delinquency.late-fee-amount",
  "delinquency.overlock-timing",
  "delinquency.overlock-when",
  "delinquency.payment-plans",
  "delinquency.redemption-until-auction",
  "delinquency.rent-extensions",
  "delinquency.waivers-manager-approval",
  "gate-code.code-assignment",
  "gate-code.code-changes",
  "insurance.coverage-requirement",
  "insurance.covers-stored-goods",
  "insurance.declaration-page-required",
  "insurance.in-house-plan",
  "insurance.own-policy-accepted",
  "insurance.own-policy-proof-method",
  "insurance.updates-channel",
  "lease-basics.lease-copy",
  "lease-basics.lease-term",
  "lease-basics.photo-id-required",
  "lease-basics.prepay",
  "lease-basics.prepay-months",
  "lease-changes.extended-absence-hold",
  "lease-changes.unit-transfers",
  "lease-changes.written-notice",
  "lock-access-loss.advance-scheduling",
  "lock-access-loss.lock-cut-fee",
  "lock-access-loss.lock-cut-fee-amount",
  "lock-access-loss.overlock-release",
  "lock-access-loss.who-may-cut",
  "maintenance-facility-issues.how-to-report",
  "maintenance-facility-issues.report-response",
  "maintenance-facility-issues.treatment-cadence",
  "move-out.broom-clean",
  "move-out.confirmation-letter",
  "move-out.deposit-refund",
  "move-out.deposit-refund-days",
  "move-out.final-month-proration",
  "move-out.moveout-request-email",
  "move-out.moveout-request-method",
  "move-out.notice-period",
  "move-out.notice-period-days",
  "move-out.walkthrough",
  "payments.accepted-methods",
  "payments.online-payments",
  "payments.partial-payments",
  "payments.payment-failure-fallback",
  "payments.rent-due-date",
  "pricing-policies.admin-fee",
  "pricing-policies.admin-fee-amount",
  "pricing-policies.card-processing-fee",
  "pricing-policies.card-processing-fee-amount",
  "pricing-policies.first-month-billing",
  "pricing-policies.price-match",
  "pricing-policies.promo-first-full-month",
  "pricing-policies.promo-timeframe",
  "pricing-policies.promotions",
  "pricing-policies.quoted-rate-days",
  "pricing-policies.rate-quote-window",
  "pricing-policies.security-deposit",
  "profanity-handling.threats-ended",
  "public-auctions.auction-platform",
  "public-auctions.lien-days",
  "public-auctions.lien-timeline",
  "public-auctions.payment-at-pickup",
  "public-auctions.pickup-window",
  "public-auctions.pickup-window-other",
  "reservation.additional-unit",
  "reservation.booking-channels",
  "reservation.hold-duration",
  "reservation.hold-duration-other",
  "reservation.past-tenants",
  "reservation.reservation-changes",
  "reservation.reservation-fee",
  "reservation.reservation-fee-amount",
  "reservation.waitlist",
  "security-features.suspicious-activity",
  "vbp.tier-highest-points",
  "vbp.tier-lowest-points",
  "vbp.tier-middle-points",
  "referral-program.referrer-incentive-type",
  "referral-program.referrer-incentive-credit-amount",
  "referral-program.referrer-incentive-gift-card-text",
  "referral-program.referrer-incentive-custom-text",
  "referral-program.referee-incentive-type",
  "referral-program.referee-incentive-credit-amount",
  "referral-program.referee-incentive-gift-card-text",
  "referral-program.referee-incentive-custom-text",
  "referral-program.tenancy-duration-days"
];

/** Surviving legacy prose/source vars fed to the extractor for the T2 pass. */
export const LEGACY_PROSE_KEYS: string[] = [
  "AccessAfterHour1",
  "AccessAfterHour2",
  "AccessGateCode1",
  "AccessGateCode2",
  "AccountBillingDispute1",
  "AccountCancelService1",
  "AccountCancelService2",
  "AccountGetLease1",
  "AccountGetLease2",
  "AccountGracePeriod1",
  "AccountGracePeriod2",
  "AccountManageInsurance1",
  "AccountManageInsurance2",
  "AccountMultipleAccounts1",
  "AccountMultipleAccounts2",
  "AccountPartialPayment1",
  "AccountPastDue1",
  "AccountPaymentExtension1",
  "AccountPaymentExtension2",
  "AccountPaymentHistory1",
  "AccountPaymentHistory2",
  "AccountPaymentIssue1",
  "AccountReservation1",
  "AccountReservation2",
  "AccountUpdateContactInformation1",
  "AccountUpdateContactInformation2",
  "AfterNavigateAccountLoginPartialPayment",
  "AfterNavigateAccountLoginPayBill",
  "AfterNavigateAccountLoginPaymentIssue",
  "AuctionsURL",
  "ChangeDueDateSelection",
  "CortexDiscountIntroductoryDetails",
  "CortexFeatureAfterHoursAccess",
  "CortexMoveOutFormURL",
  "CostAdminFee1",
  "CostCreditCardFee1",
  "CostCreditCardFee2",
  "CostInsurance1",
  "CostLateFee1",
  "CostLateFee2",
  "CostWebRates1",
  "CostWebRates2",
  "DaVinciCodeDeliveryChannel",
  "DiscountCode",
  "DiscountIntroductory1",
  "DiscountLongTerm1",
  "DiscountMilitary1",
  "DiscountSenior1",
  "DiscountStudent1",
  "FMSGateCodeChannel",
  "FeatureAuction1",
  "FeatureMaintenancePest1",
  "FeatureMaintenancePest2",
  "FeaturePadlocks1",
  "FeatureSecurity1",
  "HasAfterHoursAccess",
  "HasAuctions",
  "HasGateCodeIntegration",
  "HasInsuranceWebsiteURL",
  "HasMobileApp",
  "HasPayBillSpotPaymentPages",
  "InsuranceDisplayName",
  "InsuranceURL",
  "LLMAccessGateCode",
  "LLMAccountHoldUnit",
  "LLMFeatureAuction",
  "LLMFeatureMaintenancePest",
  "LLMLeaseMoveOutPolicy",
  "LLMLeaseOverlock",
  "LLMLeaseProrate",
  "LeaseAdminFeeAmount",
  "LeaseCancellation1",
  "LeaseClaim1",
  "LeaseClaim2",
  "LeaseContractLengths1",
  "LeaseContractLengths2",
  "LeaseContractLengths3",
  "LeaseContractLengths4",
  "LeaseCutLocks",
  "LeaseFacilityAccess1",
  "LeaseFacilityAccess2",
  "LeaseHoldUnit1",
  "LeaseHoldUnit2",
  "LeaseInsurance1",
  "LeaseInsurance2",
  "LeaseInsurance3",
  "LeaseMoveOutPolicy1",
  "LeaseMoveOutPolicy2",
  "LeaseMoveOutPolicy3",
  "LeaseOverlock1",
  "LeaseOverlock2",
  "LeaseOverlock3",
  "LeasePricing1",
  "LeasePricing2",
  "LeaseProrate1",
  "LeaseProrate2",
  "LeaseRefunds1",
  "LeaseRefunds2",
  "LeaseUnitTransfer1",
  "MoveKeyAccessLock1",
  "MoveKeyAccessLock2",
  "PaymentOptions",
  "PaymentURL",
  "ProblemBillingDispute1",
  "ProblemDamagedItems1",
  "ProblemDamagedItems2",
  "ProblemLostKey1",
  "ProblemLostKey2",
  "ProblemTheft1",
  "ProblemTheft2",
  "ProfileSettingWaitlistCollectProfile",
  "ProfileSettingWaitlistContactMethod",
  "PurchaseIdentification1",
  "ReferralIncentive",
  "SecurityAmenities",
  "SpotReferralInChatMessage",
  "SpotReferralMessage1",
  "SpotReferralMessage2",
  "SpotReferralPageBody",
  "SpotReferralShareEmailBody",
  "SpotReferralShareEmailSubject",
  "SpotReferralShareSMSBody",
  "TwoUnhandledBeforeEscalation",
  "VBPDescription1",
  "VBPDescription2",
  "VBPDescription3",
  "WaitlistTagEmailList",
  "WantsEmailReceipts",
  "WantsProfanityEscalation"
];

/** New-info — live v3 fields with no surviving legacy source; operator fills these out (future: auto-ticket). */
export const POLICY_NEW_FIELD_MAPKEYS: string[] = [
  // ENG-2461: how customers get help at an unmanned facility; net-new, no legacy source (2026-10-01)
  "access-procedures.unmanned-details",
  "access-procedures.unmanned-other",
  "access-procedures.unmanned-support",
  // ENG-3356 — the sales call script steps: net-new, no legacy source (2026-09-18)
  "accessibility-accommodations.interpreter",
  "accessibility-accommodations.large-print",
  "accessibility-accommodations.tty-relay",
  "account-info.authorized-users",
  "estate-handling.death-certificate",
  "estate-handling.family-retrieval",
  "estate-handling.lease-transfer",
  "estate-handling.letters-testamentary",
  "estate-handling.small-estate-affidavit",
  "lease-basics.co-signers",
  "lease-basics.minimum-age",
  "lease-basics.minimum-age-value",
  "lease-basics.signing-method",
  "lock-access-loss.troubleshooting-link",
  "privacy-and-data.privacy-requests",
  "privacy-and-data.retention",
  "profanity-handling.complaint-response-days",
  "profanity-handling.complaint-response-window",
  "storage-policies.lost-items",
  "storage-policies.lost-items-length",
  "third-party-management.advance-notice",
  "third-party-management.autopay-portal-carryover",
  "third-party-management.belongings-unaffected",
  "third-party-management.leases-unchanged",
  "third-party-management.rates-honored"
];

/**
 * Per-worksheet internal staff-annotation fields (`<policyKey>.staff-note`). Net-new to Policy Builder
 * with no legacy source and no operator-facing meaning — excluded from both migration and the
 * "new information to fill out" list, but enumerated so the catalog reconciliation stays exhaustive.
 */
export const POLICY_STAFF_NOTE_MAPKEYS: string[] = [
  // ENG-3356 — call-script worksheet (2026-09-18)
  "access-procedures.staff-note",
  "accessibility-accommodations.staff-note",
  "account-info.staff-note",
  "billing-questions.staff-note",
  "contact-info-update.staff-note",
  "damage-theft-claim.staff-note",
  "delinquency.staff-note",
  "estate-handling.staff-note",
  "gate-code.staff-note",
  "insurance.staff-note",
  "lease-basics.staff-note",
  "lease-changes.staff-note",
  "lock-access-loss.staff-note",
  "maintenance-facility-issues.staff-note",
  "move-out.staff-note",
  "payments.staff-note",
  "pricing-policies.staff-note",
  "privacy-and-data.staff-note",
  "profanity-handling.staff-note",
  "public-auctions.staff-note",
  "referral-program.staff-note",
  "reservation.staff-note",
  "security-features.staff-note",
  "storage-policies.staff-note",
  "third-party-management.staff-note",
  "vbp.staff-note"
];

/** T2 fields whose surviving prose rarely contains the answer; likely to fall back to new-info after extraction. */
export const POLICY_LOW_YIELD_MAPKEYS: string[] = [
  "contact-info-update.emergency-contact-required",
  "lease-basics.prepay",
  "lease-basics.prepay-months",
  "pricing-policies.price-match",
  "pricing-policies.quoted-rate-days",
  "pricing-policies.rate-quote-window",
  "reservation.past-tenants",
  "reservation.reservation-fee-amount"
];
